import ReviewsQueryBase from "@apps/reviews/queries/ReviewsQueryBase";
import ReviewsServiceBase from "@apps/reviews/services/ReviewsServiceBase";
import SdkError from "@modules/sdk/methods/SdkError";
import { ERROR_CODES } from "@modules/sdk/typedoc";
import { Observable, combineLatest, map, switchMap, take } from "rxjs";

import { GetReviewsParamsSdkDto, ReviewsSdkDto } from "../types";
import { filterGetReviewsParams } from "../utils/filterGetReviewsParams";
import { mapReviewsToSdkDto } from "../utils/mapReviewsToSdkDto";

export class ReviewsStateKitServiceWrapper {
    constructor(
        private readonly _reviewsQuery: ReviewsQueryBase,
        private readonly _reviewsService: ReviewsServiceBase
    ) {}

    /**
     * Retrieves reviews with optional filtering. Replaces the current store contents.
     *
     * @param params - (Optional) Filtering parameters
     * @returns Observable emitting the reviews collection.
     */
    getReviews(params?: GetReviewsParamsSdkDto): Observable<ReviewsSdkDto> {
        return this._reviewsService
            .getReviews(filterGetReviewsParams(params))
            .pipe(
                switchMap(() =>
                    combineLatest([
                        this._reviewsQuery.selectAll(),
                        this._reviewsQuery.paginationMeta$,
                    ])
                ),
                take(1),
                map(([reviews, paginationMeta]) =>
                    mapReviewsToSdkDto(reviews, paginationMeta)
                )
            );
    }

    /**
     * Retrieves additional reviews and appends them to the store. Ideal for infinite scroll or "load more" pagination.
     *
     * @param params - Must include the same filters as the initial `getReviews` call, with an updated `offset` param
     * @returns Observable emitting the complete reviews collection, including newly loaded items.
     */
    loadMoreReviews(params: GetReviewsParamsSdkDto): Observable<ReviewsSdkDto> {
        const filteredParams = filterGetReviewsParams(params);

        if (!filteredParams) {
            throw new SdkError(ERROR_CODES.INSUFFICIENT_ARGS);
        }

        return this._reviewsService.loadMoreReviews(filteredParams).pipe(
            switchMap(() =>
                combineLatest([
                    this._reviewsQuery.selectAll(),
                    this._reviewsQuery.paginationMeta$,
                ])
            ),
            take(1),
            map(([reviews, paginationMeta]) =>
                mapReviewsToSdkDto(reviews, paginationMeta)
            )
        );
    }
}
